<template>
    <div v-if="!loading && !hasAccess" class="max-w-4xl mx-auto py-6">
        <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6">
            <h3 class="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
                Acesso Negado
            </h3>
            <p class="text-red-700 dark:text-red-300">
                Você não tem permissão para acessar esta página. Apenas administradores podem gerenciar configurações da plataforma.
            </p>
        </div>
    </div>

    <div v-if="!loading && hasAccess" class="max-w-4xl mx-auto py-6">
        <h3 class="my-3 text-xl leading-6 font-large dark:text-white">
            Ativações Gerais
        </h3>

        <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6 mb-6">
            <h4 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Pix Automático
            </h4>

            <div v-if="stats" class="grid grid-cols-3 gap-4 mb-6">
                <div class="bg-slate-50 dark:bg-slate-700 rounded-lg p-4">
                    <p class="text-sm text-gray-600 dark:text-gray-300">Total de Usuários</p>
                    <p class="text-2xl font-bold text-gray-900 dark:text-white">
                        {{ stats.total_users }}
                    </p>
                </div>
                <div class="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                    <p class="text-sm text-gray-600 dark:text-gray-300">Pix Automático Ativo</p>
                    <p class="text-2xl font-bold text-green-600 dark:text-green-400">
                        {{ stats.enabled_users }}
                    </p>
                </div>
                <div class="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                    <p class="text-sm text-gray-600 dark:text-gray-300">Pix Automático Inativo</p>
                    <p class="text-2xl font-bold text-red-600 dark:text-red-400">
                        {{ stats.disabled_users }}
                    </p>
                </div>
            </div>

            <div class="border-t dark:border-slate-600 pt-6">
                <p class="text-sm text-gray-600 dark:text-gray-300 mb-4">
                    Use os botões abaixo para ativar ou desativar o Pix Automático para todos os usuários da plataforma.
                    Esta ação afetará todas as contas imediatamente.
                </p>

                <div class="flex gap-3">
                    <button
                        @click="enablePixRecurring"
                        :disabled="processing"
                        class="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
                    >
                        {{ processing ? 'Processando...' : 'Ativar Pix Automático para Todos' }}
                    </button>

                    <button
                        @click="disablePixRecurring"
                        :disabled="processing"
                        class="px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
                    >
                        {{ processing ? 'Processando...' : 'Desativar Pix Automático para Todos' }}
                    </button>
                </div>
            </div>
        </div>

        <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6 mb-6">
            <h4 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Apple Pay
            </h4>

            <div v-if="applePayStats" class="grid grid-cols-3 gap-4 mb-6">
                <div class="bg-slate-50 dark:bg-slate-700 rounded-lg p-4">
                    <p class="text-sm text-gray-600 dark:text-gray-300">Total de Usuários</p>
                    <p class="text-2xl font-bold text-gray-900 dark:text-white">
                        {{ applePayStats.total_users }}
                    </p>
                </div>
                <div class="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                    <p class="text-sm text-gray-600 dark:text-gray-300">Apple Pay Ativo</p>
                    <p class="text-2xl font-bold text-green-600 dark:text-green-400">
                        {{ applePayStats.enabled_users }}
                    </p>
                </div>
                <div class="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                    <p class="text-sm text-gray-600 dark:text-gray-300">Apple Pay Inativo</p>
                    <p class="text-2xl font-bold text-red-600 dark:text-red-400">
                        {{ applePayStats.disabled_users }}
                    </p>
                </div>
            </div>

            <div class="border-t dark:border-slate-600 pt-6">
                <p class="text-sm text-gray-600 dark:text-gray-300">
                    A ativação em massa do Apple Pay deixou de ser feita por esta tela (TIC2-15431).
                    Novos sellers já nascem habilitados; a base existente é atualizada por operação de banco
                    supervisionada pela infraestrutura. Para ativar ou desativar um seller específico, use a
                    configuração individual do usuário.
                </p>
            </div>
        </div>
    </div>

    <div v-if="loading" class="max-w-4xl mx-auto py-6">
        <h3 class="my-3 text-sm font-bold font-medium text-gray-400">
            Carregando estatísticas...
        </h3>
    </div>
</template>

<script>
import { useToast } from "vue-toastification";

export default {
    data() {
        return {
            loading: true,
            processing: false,
            stats: null,
            applePayStats: null,
            hasAccess: true,
        }
    },

    setup() {
        const toast = useToast();
        return { toast };
    },

    mounted() {
        this.fetchStats();
        this.fetchApplePayStats();
    },

    methods: {
        fetchStats() {
            this.loading = true;
            axios
                .get('/api/platform-settings/pix-recurring/stats')
                .then(({ data }) => {
                    this.stats = data;
                    this.hasAccess = true;
                })
                .catch((error) => {
                    if (error.response?.status === 403) {
                        this.hasAccess = false;
                        this.toast.error('Você não tem permissão para acessar esta página. Apenas administradores podem gerenciar configurações da plataforma.');
                    } else {
                        this.toast.error('Erro ao carregar estatísticas.');
                    }
                    console.error(error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        enablePixRecurring() {
            const confirmText = `Tem certeza que deseja ATIVAR o Pix Automático para todos os ${this.stats.total_users} usuários?\n\nEsta ação não pode ser desfeita facilmente.`;

            if (!confirm(confirmText)) {
                return;
            }

            this.processing = true;
            axios
                .post('/api/platform-settings/pix-recurring/enable-all')
                .then(({ data }) => {
                    this.toast.success(data.message || 'Pix Automático ativado com sucesso!');
                    this.toast.info(`${data.updated_count} usuários foram atualizados.`);
                    this.fetchStats();
                })
                .catch((error) => {
                    const message = error.response?.data?.message || 'Erro ao ativar Pix Automático.';
                    this.toast.error(message);
                    console.error(error);
                })
                .finally(() => {
                    this.processing = false;
                });
        },

        disablePixRecurring() {
            const confirmText = `Tem certeza que deseja DESATIVAR o Pix Automático para todos os ${this.stats.total_users} usuários?\n\nEsta ação afetará todas as contas imediatamente.`;

            if (!confirm(confirmText)) {
                return;
            }

            this.processing = true;
            axios
                .post('/api/platform-settings/pix-recurring/disable-all')
                .then(({ data }) => {
                    this.toast.success(data.message || 'Pix Automático desativado com sucesso!');
                    this.toast.info(`${data.updated_count} usuários foram atualizados.`);
                    this.fetchStats();
                })
                .catch((error) => {
                    const message = error.response?.data?.message || 'Erro ao desativar Pix Automático.';
                    this.toast.error(message);
                    console.error(error);
                })
                .finally(() => {
                    this.processing = false;
                });
        },

        fetchApplePayStats() {
            axios
                .get('/api/platform-settings/apple-pay/stats')
                .then(({ data }) => {
                    this.applePayStats = data;
                })
                .catch((error) => {
                    console.error(error);
                });
        },
    }
}
</script>
